/**
 * FO-307-A1 - vertaalt een wachtrij-item naar de juiste server-action en
 * normaliseert de uitkomst tot een `SyncOutcome` (voor de conflict/retry-regel).
 *
 * Client-module (geen `'use server'`): importeert de server-actions als
 * callable RSC-referenties en geeft per replay de idempotency-key mee zodat de
 * API dedupt.
 *
 * OFFLINE-SCOPE (bewust, zie STATUS-portal-audit.md): de veld-flows die vanuit
 * de UI daadwerkelijk via `runMutation` de wachtrij in gaan zijn `werkbon-day-
 * entry` (uren/notities), `checklist-submit` (veiligheids-checklist) en
 * `taak-checklist-toggle` (dagtaken afvinken). De overige takken
 * (`werkbon-attachment`, `werkbon-gereedmelden`, `werkbon-handtekening`,
 * `taak-uitvoeren`) zijn infra-compleet (idempotente server-actions + API-guard)
 * maar in v1 NIET aan een call-site gekoppeld: die "inleveren"-/aftekenstappen
 * blijven online (foto/handtekening-upload vereist connectiviteit). Aansluiten is
 * een mechanische v2-stap op dezelfde seam.
 *
 * batch-67 (FO-499 §5.3): `middel-foto` en `middel-formulier` (uitgifte/inname
 * van middelen) ZIJN wel aan een call-site gekoppeld: het formulier bewaart de
 * foto's en de verzending wanneer er geen verbinding is.
 */
import type { SyncOutcome } from '@streep/utils'
import { updateDayEntry } from '@/app/(app)/werkbonnen/_actions/update-day-entry'
import { gereedmeldWerkbon } from '@/app/(app)/werkbonnen/_actions/gereedmelden'
import { saveHandtekening } from '@/app/(app)/werkbonnen/_actions/handtekening'
import { uploadWerkbonAttachments } from '@/app/(app)/werkbonnen/_actions/upload-attachment'
import { submitChecklistOffline } from '@/app/(app)/veiligheid/_actions/veiligheid-actions'
import { uitvoerenAction, toggleChecklistAction } from '@/app/(app)/operationele-taken/_actions/taken-acties'
import { meldSchade, uploadSchadeFoto } from '@/app/(app)/mijn-voertuig/_actions/schade'
import { verstuurMiddelFormulierAction } from '@/components/middelen/middelen-actions'
import { stuurMiddelFoto } from '@/components/middelen/foto-upload'
import { afvinkenInnameAction } from '@/app/(app)/magazijn/_actions'
import type { CreateSchadeMeldingInput, InnameAfvinkenInput, MiddelFormulierSubmitPayload } from '@streep/types'
import type {
  QueuedMutation,
  DayEntryPayload,
  GereedmeldPayload,
  HandtekeningPayload,
  AttachmentPayload,
  ChecklistSubmitPayload,
  TaakUitvoerenPayload,
  TaakTogglePayload,
  SchadeMeldingPayload,
  SchadeFotoPayload,
  MiddelFotoPayload,
  MiddelFormulierPayload,
  InnameAfvinkenPayload,
} from './types'

export interface DispatchOutcome {
  outcome: SyncOutcome
  error?: string
}

/** JSON-actie-resultaat -> SyncOutcome (ok / http-status / netwerk-onbekend). */
function fromResult(res: { ok: boolean; status?: number; error?: string; code?: string }): DispatchOutcome {
  if (res.ok) return { outcome: { kind: 'http', ok: true, status: 200 } }
  // Transient (bv. een tweede PWA-tab verwerkt hetzelfde item al): 409
  // IDEMPOTENCY_IN_PROGRESS is GEEN business-conflict -> later opnieuw proberen,
  // niet als klevend conflict markeren.
  if (res.code === 'IDEMPOTENCY_IN_PROGRESS') {
    return { outcome: { kind: 'network-error' }, error: res.error }
  }
  if (typeof res.status === 'number') {
    return { outcome: { kind: 'http', ok: false, status: res.status }, error: res.error }
  }
  return { outcome: { kind: 'network-error' }, error: res.error }
}

export async function dispatchMutation(m: QueuedMutation): Promise<DispatchOutcome> {
  const key = m.idempotencyKey
  try {
    switch (m.type) {
      case 'werkbon-day-entry': {
        const p = m.payload as DayEntryPayload
        const res = await updateDayEntry({
          werkbonId: p.werkbonId,
          dayEntryId: p.dayEntryId,
          ...p.patch,
          idempotencyKey: key,
        })
        return fromResult(res)
      }
      case 'werkbon-gereedmelden': {
        const p = m.payload as GereedmeldPayload
        const res = await gereedmeldWerkbon(p.werkbonId, key)
        return fromResult(res)
      }
      case 'werkbon-handtekening': {
        const p = m.payload as HandtekeningPayload
        const res = await saveHandtekening({ ...p, idempotencyKey: key })
        return fromResult(res)
      }
      case 'werkbon-attachment': {
        const p = m.payload as AttachmentPayload
        const fd = new FormData()
        fd.append('files', p.file)
        const res = await uploadWerkbonAttachments(p.werkbonId, fd, key)
        if (res.ok && res.failed === 0) return { outcome: { kind: 'http', ok: true, status: 200 } }
        if (res.ok && res.failed > 0) {
          const code = res.firstError?.code ?? ''
          if (code === 'NETWORK') return { outcome: { kind: 'network-error' }, error: res.firstError?.message }
          // HTTP_4xx = definitieve fout; overige = opnieuw proberen.
          const is4xx = /^HTTP_4/.test(code)
          return {
            outcome: { kind: 'http', ok: false, status: is4xx ? 400 : 500 },
            error: res.firstError?.message,
          }
        }
        // {ok:false} = UNAUTHORIZED/NO_FILES -> definitieve fout.
        return { outcome: { kind: 'http', ok: false, status: 400 }, error: res.ok ? undefined : res.message }
      }
      case 'checklist-submit': {
        const p = m.payload as ChecklistSubmitPayload
        const res = await submitChecklistOffline({
          templateId: p.templateId,
          werkbonId: p.werkbonId,
          betreftEmployeeId: p.betreftEmployeeId,
          voertuigId: p.voertuigId,
          locatie: p.locatie,
          antwoorden: p.antwoorden as never[],
          idempotencyKey: key,
        })
        return fromResult(res)
      }
      case 'taak-uitvoeren': {
        const p = m.payload as TaakUitvoerenPayload
        const res = await uitvoerenAction(
          p.taakId,
          {
            uitvoerOpmerking: p.uitvoerOpmerking ?? null,
            kilometerstand: p.kilometerstand ?? null,
            uitvoerLat: p.uitvoerLat ?? null,
            uitvoerLng: p.uitvoerLng ?? null,
          },
          key,
        )
        return fromResult(res)
      }
      case 'taak-checklist-toggle': {
        const p = m.payload as TaakTogglePayload
        const res = await toggleChecklistAction(p.taakId, p.itemId, key)
        return fromResult(res)
      }
      // batch-37 schadeformulier (FO-326) — aanrijding gemeld zonder dekking.
      // De idempotency-key van het wachtrij-item is óók de melding-key: de
      // API dedupt replays (header + unique kolom).
      case 'schade-melding': {
        const p = m.payload as SchadeMeldingPayload
        const res = await meldSchade(
          { ...(p.input as CreateSchadeMeldingInput), idempotencyKey: key ?? null },
          key,
        )
        return fromResult(res)
      }
      case 'schade-foto': {
        const p = m.payload as SchadeFotoPayload
        const fd = new FormData()
        fd.append('file', p.file)
        const res = await uploadSchadeFoto(fd, { meldingKey: p.meldingKey, soort: p.soort })
        return fromResult(res)
      }
      // batch-67 middelen-inname (FO-499 §5.3) — bewijsfoto van een middel. De
      // upload is idempotent op de foto-id, dus opnieuw afspelen is veilig.
      case 'middel-foto': {
        const p = m.payload as MiddelFotoPayload
        const res = await stuurMiddelFoto(p.fotoId, p.file)
        if (res.ok) return { outcome: { kind: 'http', ok: true, status: 201 } }
        if (res.status === undefined) return { outcome: { kind: 'network-error' }, error: res.fout }
        return { outcome: { kind: 'http', ok: false, status: res.status }, error: res.fout }
      }
      // Het formulier zelf. De server verwerkt per regel: is een deel niet gelukt,
      // dan is dat geen netwerkfout maar iets dat de gebruiker moet oplossen
      // (conflict in de synchronisatielijst), en wat wel lukte staat al vast.
      case 'middel-formulier': {
        const p = m.payload as MiddelFormulierPayload
        const res = await verstuurMiddelFormulierAction(p.input as MiddelFormulierSubmitPayload)
        if (!res.ok) return fromResult(res)
        if (res.data.fouten.length > 0) {
          const eerste = res.data.fouten[0]?.message ?? 'Niet alle regels zijn verwerkt.'
          return {
            outcome: { kind: 'http', ok: false, status: 409 },
            error: `${res.data.verwerkt} verwerkt, ${res.data.fouten.length} niet: ${eerste}`,
          }
        }
        return { outcome: { kind: 'http', ok: true, status: 201 } }
      }
      // batch-69 samenstellen (FO-516 §5.5) — een afvinking op de innamelijst. De
      // API dedupt op clientMutatieId; een weigering (item al elders ingenomen,
      // lijst gesloten) is een conflict dat de gebruiker in de lijst oplost.
      case 'inname-afvinken': {
        const p = m.payload as InnameAfvinkenPayload
        const res = await afvinkenInnameAction(p.sessieId, p.itemId, p.input as InnameAfvinkenInput)
        if (res.ok) return { outcome: { kind: 'http', ok: true, status: 200 } }
        // Iemand anders boekt dit item op dit moment: later opnieuw, geen conflict.
        if (res.code === 'WORDT_VERWERKT') return { outcome: { kind: 'network-error' }, error: res.error }
        return fromResult({ ok: false, status: res.status, error: res.error, code: res.code ?? undefined })
      }
      default:
        return { outcome: { kind: 'http', ok: false, status: 400 }, error: 'Onbekend wachtrij-type' }
    }
  } catch (e) {
    // De RSC-action-POST zelf faalde (client is weer offline) -> retry.
    return { outcome: { kind: 'network-error' }, error: e instanceof Error ? e.message : 'Netwerkfout' }
  }
}
